import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Galaxy Power System',
  description = 'Galaxy Power System - Your trusted partner for batteries, inverters, solar panels & power solutions in Chidambaram, Tamil Nadu. Authorized dealer of Exide, Luminous, Amaron batteries.',
  keywords = 'battery shop chidambaram, exide battery dealer, luminous battery, amaron battery, solar panel chidambaram, inverter battery, car battery, bike battery, power solutions tamil nadu',
  canonicalUrl = 'https://galaxypowersystems.com',
  ogImage = '/assets/Galaxypower logo.png',
  ogType = 'website',
  structuredData = null,
  noIndex = false,
  pageType = 'website',
  breadcrumbs = null,
  product = null,
}) => {
  const siteName = 'Galaxy Power System';
  const fullTitle = title === siteName ? title : `${title} | ${siteName}`;

  // Default LocalBusiness structured data
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://galaxypowersystems.com',
    name: 'Galaxy Power System',
    description: 'Leading battery and power solutions provider in Chidambaram, Tamil Nadu. Authorized dealer of Exide, Luminous, Amaron batteries, solar panels, inverters and lithium batteries.',
    url: 'https://galaxypowersystems.com',
    telephone: '+91-98657-57472',
    email: 'info@galaxypowersystem.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '33/A1, VGP St',
      addressLocality: 'Chidambaram',
      addressRegion: 'Tamil Nadu',
      postalCode: '608601',
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '11.3932',
      longitude: '79.6914',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '21:00',
      },
    ],
    image: 'https://galaxypowersystems.com/assets/Galaxypower logo.png',
    priceRange: '₹₹',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Power Solutions',
      itemListElement: [
        {
          '@type': 'OfferCatalog',
          name: 'Batteries',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Exide Batteries' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Luminous Batteries' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Amaron Batteries' } },
          ],
        },
        {
          '@type': 'OfferCatalog',
          name: 'Solar Solutions',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Solar Panels' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Product', name: 'Solar Inverters' } },
          ],
        },
      ],
    },
    sameAs: [],
  };

  // Product structured data
  const productSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description || `${product.name} - High quality ${product.brand} battery available at Galaxy Power System, Chidambaram.`,
        brand: {
          '@type': 'Brand',
          name: product.brand?.toUpperCase() || 'Galaxy Power System',
        },
        image: product.image,
        category: product.category || 'Battery',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'Galaxy Power System',
          },
          priceCurrency: 'INR',
          url: canonicalUrl,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.5',
          reviewCount: '50',
        },
      }
    : null;

  // Breadcrumb structured data
  const breadcrumbSchema = breadcrumbs
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url ? `https://galaxypowersystems.com${crumb.url}` : undefined,
        })),
      }
    : null;

  // WebSite search action schema (for homepage)
  const websiteSchema =
    pageType === 'website'
      ? {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Galaxy Power System',
          url: 'https://galaxypowersystems.com',
          description: 'Leading battery and power solutions provider in Chidambaram, Tamil Nadu.',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'https://galaxypowersystems.com/?search={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          },
        }
      : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Language */}
      <html lang="en" />
      <meta httpEquiv="content-language" content="en-IN" />

      {/* Geo Meta Tags - Local SEO */}
      <meta name="geo.region" content="IN-TN" />
      <meta name="geo.placename" content="Chidambaram" />
      <meta name="geo.position" content="11.3932;79.6914" />
      <meta name="ICBM" content="11.3932, 79.6914" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Mobile & Theme */}
      <meta name="theme-color" content="#DE400E" />
      <meta name="msapplication-TileColor" content="#DE400E" />
      <meta name="apple-mobile-web-app-title" content="Galaxy Power System" />
      <meta name="application-name" content="Galaxy Power System" />

      {/* Author & Copyright */}
      <meta name="author" content="Galaxy Power System" />
      <meta name="copyright" content="Galaxy Power System" />

      {/* Structured Data - LocalBusiness (always present) */}
      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>

      {/* Structured Data - BreadcrumbList */}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}

      {/* Structured Data - Product */}
      {productSchema && (
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      )}

      {/* Structured Data - WebSite */}
      {websiteSchema && (
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      )}

      {/* Custom structured data override */}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
